import { useState, useEffect, useRef, useCallback } from 'react';
import { ShieldAlert, Wifi } from 'lucide-react';

const MAIN_ORIGIN = 'https://mallorcaklan.com';
const FALLBACK_ORIGIN = 'http://mallorcaklan.synology.me:8080';
const CHECK_INTERVAL = 45 * 1000;
const RETURN_CHECK_INTERVAL = 60 * 1000;
const FAILS_TO_SWITCH = 2;
const OKS_TO_RETURN = 2;
const REDIRECT_DELAY = 3000;

function getMode() {
  const forced = localStorage.getItem('mkfo_force_mode');
  if (forced === 'main' || forced === 'fallback') return forced;
  const h = window.location.hostname.toLowerCase();
  if (h === 'mallorcaklan.com' || h === 'www.mallorcaklan.com') return 'main';
  if (h === 'mallorcaklan.synology.me') return 'fallback';
  return 'off';
}

async function isReachable(base) {
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), 8000);
  try {
    await fetch(`${base}/api/health?t=${Date.now()}`, { signal: ctrl.signal, cache: 'no-store' });
    return true;
  } catch {
    return false;
  } finally {
    clearTimeout(timer);
  }
}

function redirectWithToken(origin) {
  const token = localStorage.getItem('token');
  const hash = token ? `#mkfo=${encodeURIComponent(token)}` : '';
  window.location.replace(origin + window.location.pathname + window.location.search + hash);
}

export default function FailoverManager() {
  const mode = getMode();
  const [switching, setSwitching] = useState(false);
  const [returning, setReturning] = useState(false);
  const failsRef = useRef(0);
  const oksRef = useRef(0);
  const redirectingRef = useRef(false);

  const checkMain = useCallback(async () => {
    if (redirectingRef.current) return;
    const ok = await isReachable('');
    if (ok) {
      failsRef.current = 0;
      return;
    }
    failsRef.current += 1;
    if (failsRef.current >= FAILS_TO_SWITCH) {
      redirectingRef.current = true;
      setSwitching(true);
      setTimeout(() => redirectWithToken(FALLBACK_ORIGIN), REDIRECT_DELAY);
    }
  }, []);

  const checkReturn = useCallback(async () => {
    if (redirectingRef.current) return;
    const ok = await isReachable(MAIN_ORIGIN);
    if (!ok) {
      oksRef.current = 0;
      return;
    }
    oksRef.current += 1;
    if (oksRef.current >= OKS_TO_RETURN) {
      redirectingRef.current = true;
      setReturning(true);
      setTimeout(() => redirectWithToken(MAIN_ORIGIN), REDIRECT_DELAY);
    }
  }, []);

  useEffect(() => {
    if (mode === 'main') {
      const initial = setTimeout(checkMain, 5000);
      const interval = setInterval(checkMain, CHECK_INTERVAL);
      const onApiError = () => checkMain();
      window.addEventListener('mk-api-network-error', onApiError);
      return () => {
        clearTimeout(initial);
        clearInterval(interval);
        window.removeEventListener('mk-api-network-error', onApiError);
      };
    }
    if (mode === 'fallback') {
      const initial = setTimeout(checkReturn, 15000);
      const interval = setInterval(checkReturn, RETURN_CHECK_INTERVAL);
      return () => {
        clearTimeout(initial);
        clearInterval(interval);
      };
    }
  }, [mode, checkMain, checkReturn]);

  if (mode === 'off') return null;

  if (switching) {
    return (
      <div className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-6" data-testid="failover-switching-overlay">
        <div className="text-center max-w-sm">
          <ShieldAlert className="h-12 w-12 text-gold mx-auto mb-4 animate-pulse" />
          <p className="text-white font-bold text-lg mb-2">Vía principal no disponible</p>
          <p className="text-white/70 text-sm">Conectando por acceso alternativo…</p>
        </div>
      </div>
    );
  }

  if (returning) {
    return (
      <div className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-6" data-testid="failover-returning-overlay">
        <div className="text-center max-w-sm">
          <Wifi className="h-12 w-12 text-green-400 mx-auto mb-4 animate-pulse" />
          <p className="text-white font-bold text-lg mb-2">Vía principal restaurada</p>
          <p className="text-white/70 text-sm">Volviendo al acceso normal…</p>
        </div>
      </div>
    );
  }

  if (mode === 'fallback') {
    return (
      <div className="fixed top-2 left-1/2 -translate-x-1/2 z-[90] pointer-events-none" data-testid="failover-fallback-badge">
        <div className="flex items-center gap-1.5 bg-amber-500/90 text-black text-[11px] font-bold px-3 py-1 rounded-full shadow-lg">
          <ShieldAlert className="h-3 w-3" />
          Acceso alternativo activo
        </div>
      </div>
    );
  }

  return null;
}
